import { after, rand, type Draw } from '../engine';
import { burst, glints, light, pop, rain, shock, wash, type Stage } from '../kit';
import { tile } from '../shapes';
import { UP, type Preset } from './types';

/*
 * Skill puzzles (wave B). They celebrate only a contest prize at least the entry (celebrationTier of the payout and
 * the entry); a solved training board is a calm «solved» panel without particles.
 */

/* ===================================== Tile match: ivory tiles rise in pairs and meet in a jade glow ===================================== */

const JADE = ['#2EE6C9', '#2F8F6A', '#F6F1E4', '#FFD166'] as const;

function pairs(
  s: Stage,
  x: number,
  y: number,
  n: number,
  speed: readonly [number, number],
  life: readonly [number, number],
): void {
  // two mirrored fans: each tile has its twin on the other side
  for (const side of [-1, 1]) {
    burst(s, x, y, {
      n,
      speed,
      angle: UP + side * 0.55,
      spread: 0.9,
      draw: tile,
      cols: ['#E5484D', '#2F8F6A', '#3B6FD8'],
      size: [7, 11],
      life,
      gravity: 520,
      drag: 0.8,
      vr: [-2.5, 2.5],
      data: () => Math.floor(rand() * 4),
    });
  }
}

export const tileMatch: Preset = {
  pal: JADE,
  light: '#F6F1E4',
  win(s) {
    light(s, s.x, s.y, 90, '#2EE6C9', 0.6, 0.7);
    pairs(s, s.x, s.y, 7, [180, 320], [0.7, 1]);
    pop('mahjong/tile-click', 0.35);
  },
  big(s) {
    wash(s.x, s.y, { col: '#2EE6C9', r: Math.max(s.W, s.H) * 0.45, dur: 1.4, alpha: 0.4 });
    pairs(s, s.x, s.y, 22, [260, 520], [1.2, 1.7]);
    glints(s, s.x, s.y, 18, 70, ['#FFFFFF', '#2EE6C9', '#FFD166']);
    pop('mahjong/tile-slide', 0.4);
  },
  mega(s) {
    tileMatch.big(s);
    after(900, () => {
      shock(s.W / 2, s.H * 0.45, { col: '#FFD166', r: Math.min(s.W, s.H) * 0.45, dur: 1, width: 4 });
      rain(s, {
        n: 60,
        over: 1800,
        vy: [140, 260],
        vr: [-2, 2],
        draw: tile,
        cols: ['#E5484D', '#2F8F6A', '#3B6FD8'],
        size: [8, 12],
        life: [2, 2.6],
      });
    });
  },
};

/* ===================================== Color sort: glazed counters bubble up in sorted streams ===================================== */

const GLAZE = ['#F2994A', '#2EC4B6', '#9B7BE0', '#F2C94C', '#EB5E7E', '#56A3F2'] as const;

/** a glazed ceramic counter: the colour, a darker rim, a light glaze highlight */
const bead: Draw = (g, p) => {
  g.fillStyle = p.col;
  g.beginPath();
  g.arc(0, 0, 1, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = 'rgba(0,0,0,.28)';
  g.lineWidth = 0.14;
  g.stroke();
  g.fillStyle = '#FFFFFF';
  g.globalAlpha *= 0.55;
  g.beginPath();
  g.ellipse(-0.32, -0.38, 0.34, 0.2, -0.6, 0, Math.PI * 2);
  g.fill();
};

export const colorSort: Preset = {
  pal: GLAZE,
  light: '#FFFFFF',
  win(s) {
    light(s, s.x, s.y, 80, '#F2C94C', 0.55, 0.6);
    burst(s, s.x, s.y, {
      n: 14,
      speed: [150, 300],
      angle: UP,
      spread: 1.4,
      draw: bead,
      size: [5, 8],
      life: [0.7, 1],
      gravity: 600,
      drag: 0.8,
    });
    pop('common/pop', 0.35);
  },
  big(s) {
    // one stream per colour, side by side: the sorted tubes pour out together
    GLAZE.forEach((c, i) => {
      const x = s.W * (0.2 + (i * 0.6) / (GLAZE.length - 1));
      burst(s, x, s.H + 10, {
        n: 8,
        speed: [420, 620],
        angle: UP,
        spread: 0.25,
        draw: bead,
        cols: [c],
        size: [6, 10],
        life: [1.3, 1.8],
        gravity: 380,
        drag: 0.6,
        over: 500,
      });
    });
    glints(s, s.x, s.y, 14, 60);
    pop('common/pop', 0.4);
  },
  mega(s) {
    colorSort.big(s);
    after(800, () => {
      wash(s.W / 2, s.H * 0.4, { col: '#F2C94C', r: Math.max(s.W, s.H) * 0.5, dur: 1.4, alpha: 0.4 });
      rain(s, {
        n: 70,
        over: 1800,
        from: 'bottom',
        vy: [-260, -140],
        draw: bead,
        size: [5, 9],
        life: [2, 2.6],
      });
    });
  },
};
